import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';

export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { requestPasswordReset } = useAuth();

  const [email, setEmail] = useState(location.state?.email || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess(true);
      // Navigate to reset password page after short delay
      setTimeout(() => {
        navigate('/reset-password', { state: { email } });
      }, 2000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('forgotPassword.requestFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card Container */}
        <div className="bg-white rounded-xl shadow-brand p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-h2 font-bold text-charcoal">
              {t('forgotPassword.title', 'Reset your password')}
            </h2>
            <p className="mt-3 text-body text-charcoal/70">
              {t(
                'forgotPassword.description',
                'Enter your email address and we will send you a verification code to reset your password.'
              )}
            </p>
          </div>

          {success ? (
            <div className="rounded-lg bg-green-50 border border-green-200 p-4">
              <p className="text-sm font-medium text-green-800">
                {t(
                  'forgotPassword.success',
                  'Verification code sent! Check your email and redirecting...'
                )}
              </p>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              )}

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-2">
                  {t('forgotPassword.email', 'Email address')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  placeholder={t('forgotPassword.emailPlaceholder', 'you@example.com')}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 px-6 border border-transparent text-body font-semibold rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? t('forgotPassword.sending', 'Sending...')
                  : t('forgotPassword.sendCode', 'Send verification code')}
              </button>

              {/* Back to login link */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {t('forgotPassword.backToLogin', 'Back to login')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
